import React, { useState, useEffect, useMemo } from 'react';
import { FaStore, FaBoxOpen, FaShoppingCart, FaDownload, FaFilter, FaMoneyBillWave } from 'react-icons/fa';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const DealerTransactionsPage = () => {
  const [dealers, setDealers] = useState([]);
  const [allStocks, setAllStocks] = useState([]);
  const [allSales, setAllSales] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtreler
  const [selectedDealer, setSelectedDealer] = useState('all'); // 'all' veya user_id
  const [dateRange, setDateRange] = useState('bugun'); // 'bugun', 'bu_hafta', 'bu_ay', 'tum_zamanlar', 'ozel'
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  useEffect(() => {
    const fetchDealerData = async () => {
      try {
        const [stocksRes, salesRes] = await Promise.all([
          api.get('/dealers/stock'),
          api.get('/dealers/sales')
        ]);

        const stocks = stocksRes.data || [];
        const sales = salesRes.data || [];

        setAllStocks(stocks);
        setAllSales(sales);

        // Benzersiz Bayi Listesini Oluştur
        const dealerMap = {};
        [...stocks, ...sales].forEach(item => {
          if (item.user_id && item.dealer_name && !dealerMap[item.user_id]) {
            dealerMap[item.user_id] = { id: item.user_id, name: item.dealer_name };
          }
        });
        const dealerList = Object.values(dealerMap).sort((a, b) => a.name.localeCompare(b.name));
        setDealers(dealerList);

      } catch (error) {
        console.error(error);
        toast.error('Gerekli veriler yüklenemedi.');
      } finally {
        setLoading(false);
      }
    };

    fetchDealerData();
  }, []);

  // Tarihleri hesapla
  const getFilterDates = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const endOfToday = new Date();
    endOfToday.setHours(23, 59, 59, 999);

    let start = new Date(0); // varsayılan çok eski
    let end = new Date(endOfToday);

    if (dateRange === 'bugun') {
      start = new Date(today);
    } else if (dateRange === 'bu_hafta') {
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Pazartesi'den başla
      start = new Date(today.setDate(diff));
      start.setHours(0,0,0,0);
    } else if (dateRange === 'bu_ay') {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
    } else if (dateRange === 'ozel') {
      if (customStartDate) start = new Date(customStartDate);
      if (customEndDate) {
        end = new Date(customEndDate);
        end.setHours(23, 59, 59, 999);
      }
    }
    return { start, end };
  };

  // Filtrelenmiş Satışlar
  const filteredSales = useMemo(() => {
    let sales = [...allSales];
    
    // 1. Bayi Filtresi
    if (selectedDealer !== 'all') {
      sales = sales.filter(s => String(s.user_id) === String(selectedDealer));
    }

    // 2. Tarih Filtresi
    if (dateRange !== 'tum_zamanlar') {
      const { start, end } = getFilterDates();
      sales = sales.filter(s => {
        const dDate = new Date(s.sale_date);
        return dDate >= start && dDate <= end;
      });
    }

    // Yeniden eskiye sırala
    sales.sort((a, b) => new Date(b.sale_date) - new Date(a.sale_date));
    return sales;
  }, [allSales, selectedDealer, dateRange, customStartDate, customEndDate]);

  // Filtrelenmiş Stoklar (Sadece Kalan Stok) - Tarihe bağlı değil, sadece Bayi filtresine bağlı
  const filteredStocks = useMemo(() => {
    let stocks = [...allStocks];
    if (selectedDealer !== 'all') {
      stocks = stocks.filter(s => String(s.user_id) === String(selectedDealer));
    }
    return stocks;
  }, [allStocks, selectedDealer]);

  // Kart İstatistikleri
  const stats = useMemo(() => {
    const totalRev = filteredSales.reduce((acc, s) => acc + (Number(s.total_price) || 0), 0);
    const totalQty = filteredSales.reduce((acc, s) => acc + (Number(s.quantity) || 0), 0);
    const totalStock = filteredStocks.reduce((acc, s) => acc + (Number(s.stock_quantity) || 0), 0);
    return { totalRev, totalQty, totalStock };
  }, [filteredSales, filteredStocks]);

  // Grafik Verisi Oluştur
  const chartDataObj = useMemo(() => {
    const dataByDate = {};

    filteredSales.forEach(s => {
      const dateStr = new Date(s.sale_date).toLocaleDateString('tr-TR');
      if (!dataByDate[dateStr]) {
        dataByDate[dateStr] = { rev: 0, qty: 0 };
      }
      dataByDate[dateStr].rev += Number(s.total_price) || 0;
      dataByDate[dateStr].qty += Number(s.quantity) || 0;
    });

    const dates = Object.keys(dataByDate).sort((a, b) => { // DD.MM.YYYY sort mapping
      const [dA, mA, yA] = a.split('.');
      const [dB, mB, yB] = b.split('.');
      return new Date(yA, mA - 1, dA) - new Date(yB, mB - 1, dB);
    });

    const revs = dates.map(d => dataByDate[d].rev);
    const qtys = dates.map(d => dataByDate[d].qty);

    return {
      labels: dates.length > 0 ? dates : ['Veri Yok'],
      datasets: [
        {
          type: 'line',
          label: 'Günlük Ciro (TL)',
          borderColor: 'rgb(54, 162, 235)',
          backgroundColor: 'rgba(54, 162, 235, 0.5)',
          borderWidth: 3,
          yAxisID: 'y',
          data: revs.length > 0 ? revs : [0],
          fill: false,
          tension: 0.3
        },
        {
          type: 'bar',
          label: 'Satılan Ürün (Adet)',
          backgroundColor: 'rgba(255, 99, 132, 0.7)',
          yAxisID: 'y1',
          data: qtys.length > 0 ? qtys : [0],
          borderRadius: 4
        }
      ]
    };
  }, [filteredSales]);

  // Excel (CSV) Çıktı Alma İşlemi
  const exportToCSV = () => {
    let csvContent = "data:text/csv;charset=utf-8,\uFEFF"; // BOM for Excel Turkish Char Support
    csvContent += "Bayi Adı;Tarih;Ürün;Satış Adedi;Birim;Tutar(TL)\n";
    
    filteredSales.forEach(s => {
      const dealer = (s.dealer_name || "").replace(/;/g, ",");
      const dt = new Date(s.sale_date).toLocaleString('tr-TR').replace(/;/g, "-");
      const prod = (s.product_name || "").replace(/;/g, ",");
      const qty = s.quantity || 0;
      const unit = "Adet"; // can be dynamic if saved
      const total = s.total_price || 0;
      csvContent += `"${dealer}";"${dt}";"${prod}";${qty};"${unit}";${total}\n`;
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    // Dinamik isim oluştur
    const fileD = selectedDealer === 'all' ? 'TumBayiler' : dealers.find(d=>String(d.id)===String(selectedDealer))?.name.replace(/ /g,'_');
    link.setAttribute("download", `Satis_Gecmisi_${fileD}_${new Date().toLocaleDateString('tr-TR')}.csv`);
    
    document.body.appendChild(link); // Required for FF
    link.click();
    document.body.removeChild(link);
  };


  if (loading) return <div className="p-4">Yükleniyor...</div>;

  return (
    <div className="container-fluid mt-4 mb-5">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4">
        <h2>Bayi Performans ve Log Ekranı</h2>
      </div>

      {/* Üst Filtre Alanı */}
      <div className="card shadow-sm mb-4 border-0" style={{backgroundColor: '#f8f9fa'}}>
        <div className="card-body">
          <div className="row g-3">
            {/* Bayi Seçimi */}
            <div className="col-12 col-md-4">
              <label className="form-label fw-bold text-secondary"><FaStore className="me-2"/>Bayi Filtresi</label>
              <select 
                className="form-select border-primary shadow-sm" 
                value={selectedDealer} 
                onChange={(e) => setSelectedDealer(e.target.value)}
              >
                <option value="all">-- Tüm Bayiler (Kümülatif) --</option>
                {dealers.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>

            {/* Tarih Aralığı */}
            <div className="col-12 col-md-4">
              <label className="form-label fw-bold text-secondary"><FaFilter className="me-2"/>Tarih Aralığı</label>
              <select 
                className="form-select shadow-sm" 
                value={dateRange} 
                onChange={(e) => setDateRange(e.target.value)}
              >
                <option value="bugun">Bugün</option>
                <option value="bu_hafta">Bu Hafta</option>
                <option value="bu_ay">Bu Ay</option>
                <option value="tum_zamanlar">Tüm Zamanlar</option>
                <option value="ozel">Özel Tarih Aralığı</option>
              </select>
            </div>

            {/* Özel Tarihler (Eğer "Özel" Seçiliyse) */}
            {dateRange === 'ozel' && (
              <div className="col-12 col-md-4 d-flex align-items-end gap-2">
                <div className="w-50">
                  <label className="form-label small mb-1">Başlangıç</label>
                  <input type="date" className="form-control form-control-sm" value={customStartDate} onChange={e=>setCustomStartDate(e.target.value)} />
                </div>
                <div className="w-50">
                  <label className="form-label small mb-1">Bitiş</label>
                  <input type="date" className="form-control form-control-sm" value={customEndDate} onChange={e=>setCustomEndDate(e.target.value)} />
                </div>
              </div>
            )}
            
            {/* CSV Çıktı Butonu sağa yaslı */}
            {(dateRange !== 'ozel' || dateRange === 'ozel') && (
              <div className="col-12 col-md-4 d-flex align-items-end justify-content-md-end mt-3 mt-md-0">
                <button onClick={exportToCSV} className="btn btn-success shadow-sm d-flex align-items-center">
                  <FaDownload className="me-2" />
                  Excel (CSV) İndir
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* KPI KARTLARI (Özet) */}
      <div className="row mb-4">
        <div className="col-md-4 mb-3 mb-md-0">
          <div className="card text-white bg-primary h-100 shadow-sm border-0" style={{borderRadius: '15px'}}>
            <div className="card-body d-flex align-items-center justify-content-between p-4">
              <div>
                <h6 className="card-title opacity-75 fw-bold mb-1">Dönem Cirosu</h6>
                <h3 className="fw-bold mb-0">
                  {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(stats.totalRev)}
                </h3>
              </div>
              <FaMoneyBillWave size={45} className="opacity-50" />
            </div>
          </div>
        </div>

        <div className="col-md-4 mb-3 mb-md-0">
          <div className="card text-white h-100 shadow-sm border-0" style={{backgroundColor: '#e83e8c', borderRadius: '15px'}}>
            <div className="card-body d-flex align-items-center justify-content-between p-4">
              <div>
                <h6 className="card-title opacity-75 fw-bold mb-1">Satılan Net Ürün Adedi</h6>
                <h3 className="fw-bold mb-0">
                  {stats.totalQty} Adet
                </h3>
              </div>
              <FaShoppingCart size={45} className="opacity-50" />
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card text-white bg-success h-100 shadow-sm border-0" style={{borderRadius: '15px'}}>
            <div className="card-body d-flex align-items-center justify-content-between p-4">
              <div>
                <h6 className="card-title opacity-75 fw-bold mb-1">Bayilerdeki Canlı Stok</h6>
                <h3 className="fw-bold mb-0">
                  {stats.totalStock} Adet
                </h3>
              </div>
              <FaBoxOpen size={45} className="opacity-50" />
            </div>
          </div>
        </div>
      </div>

      {/* BÜYÜK GRAFİK ALANI */}
      <div className="card shadow-sm mb-4 border-0">
        <div className="card-header bg-white border-0 pt-4 pb-0">
          <h5 className="mb-0 fw-bold text-secondary">Zamana Bağlı Satış Trendi</h5>
        </div>
        <div className="card-body p-4">
            <div style={{ height: '350px' }}>
              <Chart 
                type='bar'
                data={chartDataObj} 
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  interaction: {
                    mode: 'index',
                    intersect: false,
                  },
                  scales: {
                    y: {
                      type: 'linear',
                      display: true,
                      position: 'left',
                      title: { display: true, text: 'Ciro (TL)' }
                    },
                    y1: {
                      type: 'linear',
                      display: true,
                      position: 'right',
                      grid: { drawOnChartArea: false },
                      title: { display: true, text: 'Adet' }
                    },
                  }
                }} 
              />
            </div>
        </div>
      </div>

      {/* ALT TABLOLAR */}
      <div className="row">
        {/* Satış Listesi (Sola) */}
        <div className="col-md-8 mb-4 mb-md-0">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-header bg-light border-0 py-3">
              <h6 className="mb-0 fw-bold d-flex align-items-center">
                <FaShoppingCart className="me-2 text-primary" /> Satış Geçmişi Logs
              </h6>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table className="table table-hover align-middle mb-0">
                  <thead className="bg-white sticky-top shadow-sm" style={{zIndex: 1}}>
                    <tr>
                      <th className="px-3">Tarih</th>
                      <th>Bayi</th>
                      <th>Ürün</th>
                      <th className="text-center">Adet</th>
                      <th className="text-end px-3">Tutar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSales.map(sale => (
                      <tr key={sale.id}>
                        <td className="px-3 text-muted small">{new Date(sale.sale_date).toLocaleString('tr-TR')}</td>
                        <td><span className="fw-bold">{sale.dealer_name}</span></td>
                        <td>{sale.product_name}</td>
                        <td className="text-center"><span className="badge bg-secondary">{sale.quantity}</span></td>
                        <td className="text-end fw-bold px-3 text-success">
                          {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(sale.total_price)}
                        </td>
                      </tr>
                    ))}
                    {filteredSales.length === 0 && (
                      <tr>
                        <td colSpan="5" className="text-center p-4 text-muted">Belirtilen kriterlere uygun satış kaydı yok.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Canlı Stok Listesi (Sağa) */}
        <div className="col-md-4">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-header bg-light border-0 py-3">
              <h6 className="mb-0 fw-bold d-flex align-items-center">
                <FaBoxOpen className="me-2 text-success" /> Canlı Stok Durumu
              </h6>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table className="table table-hover align-middle mb-0">
                  <thead className="bg-white sticky-top shadow-sm" style={{zIndex: 1}}>
                    <tr>
                      <th className="px-3">Bayi & Ürün</th>
                      <th className="text-end px-3">Eldeki Stok</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStocks.map(stock => (
                      <tr key={stock.id}>
                        <td className="px-3">
                          <div className="fw-bold text-dark">{stock.dealer_name}</div>
                          <div className="small text-muted">{stock.product_name}</div>
                        </td>
                        <td className="text-end px-3">
                          <span className={`badge ${stock.stock_quantity > 10 ? 'bg-success' : 'bg-danger'} rounded-pill px-3 py-2`}>
                            {stock.stock_quantity} {stock.unit || 'Adet'}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {filteredStocks.length === 0 && (
                      <tr>
                        <td colSpan="2" className="text-center p-4 text-muted">Stok bilgisi bulunmamaktadır.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default DealerTransactionsPage;
